/**
 * 菜单相关本地缓存同步（localStorage: MENUS_BUTTONS_CACHE / MENUS_DATA_CACHE）
 *
 * 背景：
 * 子应用的按钮鉴权和菜单路径解析都读 localStorage，key 固定两个：
 * 1. MENUS_BUTTONS_CACHE：{ [菜单code]: [按钮id, ...] } —— 按钮权限
 * 2. MENUS_DATA_CACHE：   { [菜单code]: 路由url }      —— code 反查路由（getMenuPathByCode）
 *
 * 这两个 key 原本只有 iot-web 在自己的 render 阶段（/menu/user-own/tree 请求完成后
 * 调 saveMenusCache）才会写。于是出现一个问题：
 * 用户如果先进其它子应用（iot-cascade-web、atomic-center-web 等，它们只读不写），
 * 且从未激活过 iot-web，缓存就是空的，按钮权限与路径反查全部失效。
 *
 * 做法：
 * 基座 Main 初始化时本来就会请求同一个接口 /menu/user-own/tree（baseUserOwnTree），
 * 返回的就是当前用户的完整菜单树（节点上带 buttons / appId / url），
 * 这里把同一份响应按子应用的口径落盘，保证任意子应用首次激活时缓存已就绪。
 * 写入时机必须在拉起子应用（initRender）之前。
 *
 * 两个 key 的写入策略刻意不同：
 * - 按钮权限：整体覆盖。它是「当前用户能干什么」的权限快照，必须与本次登录用户严格一致，
 *   合并会残留上一个用户的授权。
 * - 路径映射：合并。子应用会往里塞详情页/隐藏页（如 xxx/Detail、xxx/Save）这些基座树里
 *   没有、只有子应用自己按组件注册表才能生成的条目；基座整份覆盖会把这些条目抹掉。
 */

export const MENUS_BUTTONS_CACHE_KEY = "MENUS_BUTTONS_CACHE";
export const MENUS_DATA_CACHE_KEY = "MENUS_DATA_CACHE";

type ButtonItem = string | { id?: string };

export interface MenuNode {
  code?: string;
  url?: string;
  appId?: string;
  buttons?: ButtonItem[];
  children?: MenuNode[];
  [key: string]: any;
}

export type ButtonsPermissionCache = Record<string, string[]>;
export type MenusDataCache = Record<string, string>;

const getButtonId = (button?: ButtonItem): string => {
  if (!button) {
    return "";
  }
  return typeof button === "string" ? button : button.id || "";
};

/** 深度遍历菜单树（先序），回调拿到每个节点 */
const walkMenus = (nodes: MenuNode[] = [], handler: (node: MenuNode) => void) => {
  nodes.forEach((node) => {
    if (!node) {
      return;
    }
    handler(node);
    if (Array.isArray(node.children) && node.children.length) {
      walkMenus(node.children, handler);
    }
  });
};

const readJsonCache = <T extends object>(key: string, fallback: T): T => {
  try {
    const cacheStr = localStorage.getItem(key) || "";
    if (!cacheStr) {
      return fallback;
    }
    const cache = JSON.parse(cacheStr);
    return cache && typeof cache === "object" ? cache : fallback;
  } catch (error) {
    console.warn(`[menuCache] 解析 ${key} 失败`, error);
    return fallback;
  }
};

const writeJsonCache = (key: string, value: object): boolean => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`[menuCache] 写入 ${key} 失败`, error);
    return false;
  }
};

/**
 * 菜单树 -> 按钮权限映射 { code: [buttonId] }
 * 与子应用 saveMenusCache 的口径一致：只收带 buttons 的节点，没有按钮的菜单不占位
 */
export const buildButtonsCache = (menuTree: MenuNode[] = []): ButtonsPermissionCache => {
  const buttons: ButtonsPermissionCache = {};
  walkMenus(menuTree, (node) => {
    const buttonIds = (Array.isArray(node.buttons) ? node.buttons : [])
      .map(getButtonId)
      .filter(Boolean);
    if (node.code && buttonIds.length) {
      buttons[node.code] = buttonIds;
    }
  });
  return buttons;
};

/**
 * 菜单树 -> code 反查路由映射 { code: url }
 * 与子应用一致：集成型菜单（appId 存在）的 url 需要拼上 /{appId} 前缀
 */
export const buildMenuDataCache = (menuTree: MenuNode[] = []): MenusDataCache => {
  const menusData: MenusDataCache = {};
  walkMenus(menuTree, (node) => {
    if (!node.code || !node.url) {
      return;
    }
    menusData[node.code] = node.appId ? `/${node.appId}${node.url}` : node.url;
  });
  return menusData;
};

export const getButtonsCache = (): ButtonsPermissionCache =>
  readJsonCache<ButtonsPermissionCache>(MENUS_BUTTONS_CACHE_KEY, {});

export const getMenusDataCache = (): MenusDataCache =>
  readJsonCache<MenusDataCache>(MENUS_DATA_CACHE_KEY, {});

/**
 * 写按钮权限缓存（整体覆盖）
 * @param menuTree 后端 /menu/user-own/tree 返回的原始菜单树，
 *                 不要传过滤后的菜单——过滤只影响导航展示，不代表权限本身
 */
export const saveButtonsCache = (menuTree?: MenuNode[]): boolean => {
  if (!Array.isArray(menuTree) || menuTree.length === 0) {
    // 接口没数据时不写，避免把子应用已经写好的缓存清空
    return false;
  }
  return writeJsonCache(MENUS_BUTTONS_CACHE_KEY, buildButtonsCache(menuTree));
};

/**
 * 写 code 反查路由缓存（合并）
 * 保留子应用写入的详情页/隐藏页条目，只用基座拿到的菜单覆盖同名 code
 */
export const saveMenuDataCache = (menuTree?: MenuNode[]): boolean => {
  if (!Array.isArray(menuTree) || menuTree.length === 0) {
    return false;
  }
  const built = buildMenuDataCache(menuTree);
  if (!Object.keys(built).length) {
    return false;
  }
  return writeJsonCache(MENUS_DATA_CACHE_KEY, {
    ...getMenusDataCache(),
    ...built,
  });
};

/** 一次写齐两个缓存，基座初始化菜单后调用 */
export const saveMenusCache = (menuTree?: MenuNode[]): void => {
  saveButtonsCache(menuTree);
  saveMenuDataCache(menuTree);
};

/** 清空菜单相关缓存（退出登录等场景可用） */
export const clearMenusCache = () => {
  try {
    localStorage.removeItem(MENUS_BUTTONS_CACHE_KEY);
    localStorage.removeItem(MENUS_DATA_CACHE_KEY);
  } catch (error) {
    console.warn("[menuCache] 清理缓存失败", error);
  }
};
